import { useEffect, useMemo, useRef } from 'react';
import { safeLocalStorage } from '@/lib/safeLocalStorage';
import { dataTablePreferencesKey } from '@/lib/safeLocalStorage/schema';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  FilterOperators,
  type FilterOperator,
  type TableQueryState,
} from '../types';

const isBrowser = (): boolean => typeof window !== 'undefined';

const isValidOperator = (value: string): value is FilterOperator => {
  return Object.values(FilterOperators).includes(value as FilterOperator);
};

const toTableQueryState = (value: unknown): TableQueryState | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as TableQueryState;
  if (!candidate.pagination || !candidate.filters) {
    return null;
  }

  const { pageIndex, pageSize } = candidate.pagination;
  if (typeof pageIndex !== 'number' || typeof pageSize !== 'number') {
    return null;
  }

  if (!Array.isArray(candidate.filters)) {
    return null;
  }

  const validFilters = candidate.filters.filter(filter => {
    return (
      typeof filter.field === 'string' &&
      isValidOperator(filter.operator) &&
      Array.isArray(filter.value)
    );
  });

  const validSortOrder = new Set<string>(Object.values(SortOrder));
  const sort =
    candidate.sort &&
    typeof candidate.sort.field === 'string' &&
    typeof candidate.sort.order === 'string' &&
    validSortOrder.has(candidate.sort.order)
      ? {
          field: candidate.sort.field,
          order: candidate.sort.order as SortOrder,
        }
      : null;

  const search = typeof candidate.search === 'string' ? candidate.search : '';

  return {
    pagination: { pageIndex, pageSize },
    filters: validFilters,
    sort,
    search,
    expandedRowIds: new Set(),
  };
};

const readPreferences = (
  tableId: string,
  validSortFields?: Set<string>
): TableQueryState | null => {
  if (!isBrowser()) {
    return null;
  }

  const raw = safeLocalStorage.getItem(dataTablePreferencesKey(tableId));
  const state = toTableQueryState(raw);

  // Validate sort field against allowed fields if provided
  if (
    state?.sort &&
    validSortFields &&
    !validSortFields.has(state.sort.field)
  ) {
    return {
      ...state,
      sort: null,
    };
  }

  return state;
};

const writePreferences = (tableId: string, state: TableQueryState): void => {
  if (!isBrowser()) {
    return;
  }

  safeLocalStorage.setItem(dataTablePreferencesKey(tableId), state);
};

export interface UseDataTableLocalPreferencesOptions {
  tableId: string;
  state: TableQueryState;
  onHydrateState?: (state: TableQueryState) => void;
  enabled?: boolean;
  /**
   * Set of valid sort field names. If provided, persisted sort state
   * will be validated against this set. Invalid sort fields will be
   * dropped (not hydrated) to prevent API validation errors.
   */
  validSortFields?: Set<string>;
}

export interface UseDataTableLocalPreferencesResult {
  persistedState: TableQueryState | null;
}

export const useDataTableLocalPreferences = ({
  tableId,
  state,
  onHydrateState,
  enabled = true,
  validSortFields,
}: UseDataTableLocalPreferencesOptions): UseDataTableLocalPreferencesResult => {
  const hasHydratedRef = useRef(false);

  const persistedState = useMemo(() => {
    if (!enabled) {
      return null;
    }

    return readPreferences(tableId, validSortFields);
  }, [tableId, enabled, validSortFields]);

  useEffect(() => {
    if (hasHydratedRef.current) {
      return;
    }

    if (persistedState && onHydrateState) {
      // Row expansion is not a persisted preference: `toTableQueryState`
      // always reads it back as an empty set. Since SET_STATE replaces the
      // whole state, hydrating the raw payload would wipe any expansion the
      // table was mounted with (e.g. a deep-linked row), and only on visits
      // where preferences already exist — a first-visit-only bug. Keep the
      // live value instead.
      onHydrateState({
        ...persistedState,
        expandedRowIds: state.expandedRowIds,
      });
    }

    hasHydratedRef.current = true;
    // `state.expandedRowIds` is read once — `hasHydratedRef` makes this effect
    // a one-shot, so listing it cannot cause a re-hydrate.
  }, [persistedState, onHydrateState, state.expandedRowIds]);

  useEffect(() => {
    if (!hasHydratedRef.current) {
      return;
    }

    if (!enabled) {
      return;
    }

    writePreferences(tableId, state);
  }, [tableId, state, enabled]);

  return {
    persistedState,
  };
};
